var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Stack, TextField, Typography } from '@mui/material';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { formatNumberByLocale } from '../../../util';
import { calcPaidDaysPerWeek, calcPaidHoursPerWeekFromDayAndWeek, calcPaidHoursPerYear, round2 } from '../../../util/shrinkageCalculations';
import { NumberInput, UnControlledFormField } from '../../common';
import { STAFF_SETTINGS_LIMITS } from './validation';
export var WorkHoursFields = function (_a) {
  var value = _a.value,
    onChange = _a.onChange,
    _b = _a.disabled,
    disabled = _b === void 0 ? false : _b;
  var t = useTranslation('orgConfig', {
    keyPrefix: 'staffSettings'
  }).t;
  var dayLimits = STAFF_SETTINGS_LIMITS.paidHoursPerDay,
    weekLimits = STAFF_SETTINGS_LIMITS.workingDaysPerWeek,
    yearLimits = STAFF_SETTINGS_LIMITS.workingDaysPerYear;
  var paidHoursPerDay = Number.parseFloat(value.paidHoursPerDay) || 0;
  var workingDaysPerWeek = Number.parseFloat(value.workingDaysPerWeek) || 0;
  var workingDaysPerYear = Number.parseFloat(value.workingDaysPerYear) || 0;
  var derived = useMemo(function () {
    var paidHoursPerWeek = calcPaidHoursPerWeekFromDayAndWeek(paidHoursPerDay, workingDaysPerWeek);
    var paidDaysPerWeek = calcPaidDaysPerWeek(workingDaysPerWeek);
    var paidHoursPerYear = calcPaidHoursPerYear(paidHoursPerDay, workingDaysPerYear);
    return {
      paidHoursPerWeek: paidHoursPerWeek,
      paidDaysPerWeek: paidDaysPerWeek,
      paidHoursPerYear: paidHoursPerYear
    };
  }, [paidHoursPerDay, workingDaysPerWeek, workingDaysPerYear]);
  var readOnlySx = {
    backgroundColor: 'action.hover',
    cursor: 'default',
    color: 'text.disabled',
    pointerEvents: 'none',
    userSelect: 'none',
    '& input': {
      userSelect: 'none',
      WebkitUserSelect: 'none',
      color: 'inherit'
    }
  };
  var setField = function (field, v) {
    onChange(function (state) {
      var _a;
      return __assign(__assign({}, state), (_a = {}, _a[field] = v == null ? '' : String(v), _a));
    });
  };
  var fieldColumnSx = {
    flex: 1,
    minWidth: 0
  };
  var formatDerived = function (v) {
    return v > 0 ? formatNumberByLocale(round2(v)) : '----';
  };
  return _jsxs(Stack, __assign({
    spacing: 2
  }, {
    children: [_jsxs(Stack, __assign({
      direction: "row",
      spacing: 3,
      alignItems: "flex-end",
      useFlexGap: true
    }, {
      children: [_jsx(UnControlledFormField, __assign({
        label: t('paidHoursPerDay', {
          defaultValue: 'Paid hours/day'
        }),
        required: true,
        sx: fieldColumnSx
      }, {
        children: _jsx(NumberInput, {
          fullWidth: true,
          min: 0,
          max: dayLimits.max,
          decimal: 2,
          value: value.paidHoursPerDay,
          disabled: disabled,
          onChange: function (v) {
            return setField('paidHoursPerDay', v);
          }
        })
      })), _jsx(UnControlledFormField, __assign({
        label: t('workingDaysPerWeek', {
          defaultValue: 'Working days/week'
        }),
        required: true,
        sx: fieldColumnSx
      }, {
        children: _jsx(NumberInput, {
          fullWidth: true,
          min: weekLimits.min,
          max: weekLimits.max,
          decimal: 2,
          value: value.workingDaysPerWeek,
          disabled: disabled,
          onChange: function (v) {
            return setField('workingDaysPerWeek', v);
          }
        })
      })), _jsx(UnControlledFormField, __assign({
        label: t('workingDaysPerYear', {
          defaultValue: 'Working days/year'
        }),
        required: true,
        sx: fieldColumnSx
      }, {
        children: _jsx(NumberInput, {
          fullWidth: true,
          min: yearLimits.min,
          max: yearLimits.max,
          decimal: 2,
          value: value.workingDaysPerYear,
          disabled: disabled,
          onChange: function (v) {
            return setField('workingDaysPerYear', v);
          }
        })
      }))]
    })), _jsxs(Stack, __assign({
      direction: "row",
      spacing: 3,
      alignItems: "flex-end",
      useFlexGap: true
    }, {
      children: [_jsx(UnControlledFormField, __assign({
        label: t('paidHoursPerWeek', {
          defaultValue: 'Paid hours/week (FTE)'
        }),
        sx: fieldColumnSx
      }, {
        children: _jsx(TextField, {
          fullWidth: true,
          size: "small",
          value: formatDerived(derived.paidHoursPerWeek),
          InputProps: {
            readOnly: true,
            sx: readOnlySx
          },
          inputProps: {
            tabIndex: -1,
            'aria-readonly': true
          }
        })
      })), _jsx(UnControlledFormField, __assign({
        label: t('paidDaysPerWeek', {
          defaultValue: 'Paid days/week (FTE)'
        }),
        sx: fieldColumnSx
      }, {
        children: _jsx(TextField, {
          fullWidth: true,
          size: "small",
          value: formatDerived(derived.paidDaysPerWeek),
          InputProps: {
            readOnly: true,
            sx: readOnlySx
          },
          inputProps: {
            tabIndex: -1,
            'aria-readonly': true
          }
        })
      })), _jsx(UnControlledFormField, __assign({
        label: t('paidHoursPerYear', {
          defaultValue: 'Paid hours/year (FTE)'
        }),
        sx: fieldColumnSx
      }, {
        children: _jsx(TextField, {
          fullWidth: true,
          size: "small",
          value: formatDerived(derived.paidHoursPerYear),
          InputProps: {
            readOnly: true,
            sx: readOnlySx
          },
          inputProps: {
            tabIndex: -1,
            'aria-readonly': true
          }
        })
      }))]
    }))]
  }));
};
export var StaffSettingsSection = function (_a) {
  var title = _a.title,
    children = _a.children,
    sx = _a.sx;
  return _jsxs(Box, __assign({
    sx: __assign({
      border: 1,
      borderColor: 'divider',
      borderRadius: 1,
      p: 3,
      mb: 3,
      bgcolor: 'background.paper'
    }, sx)
  }, {
    children: [_jsx(Typography, __assign({
      variant: "subtitle2",
      sx: {
        mb: 2
      }
    }, {
      children: title
    })), children]
  }));
};